<script lang="ts" setup>
import { FormState } from '@/composition/forms'
import { currency } from '@ghl/clientportal-core'
import {
  UIModal,
  UIModalHeader,
  UITextSmNormal,
  useNotification,
} from '@gohighlevel/ghl-ui'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { app } from '../../../composition/app'
import { GroupState } from '../../../composition/groups'
import {
  DEFAULT_GROUP_COVER_IMAGE,
  UI_PAYMENT_ELEMENT_EVENT,
} from '../../../helper/const'
import TaxNotice from '../TaxNotice.vue'
import {
  GroupType,
  MemberStatus,
  MembershipType,
  SubscriptionType,
} from '../../../models/Groups'
import { useGlobalStore } from '../../../store/app'
import { capitalizeWord } from '../../../util/stringFormatting'
import CommunityButton from '../buttons/CommunityButton.vue'

const MembershipQuestions = defineAsyncComponent(
  () =>
    import('@/components/groups/membershipQuestions/MembershipQuestions.vue')
)
const PaymentModal = defineAsyncComponent(
  () => import('@/components/groups/payments/PaymentModal.vue')
)
const PaymentSuccessfulModal = defineAsyncComponent(
  () => import('@/components/groups/payments/PaymentSuccessfulModal.vue')
)
const LoginModal = defineAsyncComponent(
  () => import('@/components/login/LoginModal.vue')
)

const appStore = useGlobalStore()
const joinGroupLoader = ref(false)
const showMembershipQuestions = ref(false)

const coverUrl = computed(() => {
  return GroupState.state.groupData.branding?.cover || DEFAULT_GROUP_COVER_IMAGE
})
const { t } = useI18n()

const props = defineProps({
  show: Boolean,
  showLogin: Boolean,
  actionText: {
    type: String,
    required: false,
  },
})
const emit = defineEmits(['update:confirmed', 'update:show'])
/** Checks if member needs to answer membership questions before joining */
const hasPendingMembershipQuestions = computed(() => {
  return (
    GroupState.state.groupData?.settings?.membershipQuestionsEnabled &&
    [MemberStatus.REQUESTED, MemberStatus.PAYMENT_RECEIVED].includes(
      GroupState.state?.userStatus
    ) &&
    FormState.state?.membershipQuestions?.questions?.length > 0 &&
    !GroupState.state?.userMembershipAnswers
  )
})
const actionText = computed(() => {
  if (
    GroupState.state?.userStatus === MemberStatus.REQUESTED &&
    !hasPendingMembershipQuestions.value
  ) {
    return t('joinGroupModal.notPartOfGroup')
  }
  return (
    props.actionText ??
    (hasPendingMembershipQuestions.value &&
    GroupState.state?.userStatus === MemberStatus.REQUESTED
      ? t('joinGroupModal.pleaseAnswerMembershipQuestions')
      : t('joinGroupModal.nonMemberMessage'))
  )
})
const showModal = ref(false)
onMounted(() => {
  showModal.value = props.show
  showLoginModal.value = props.showLogin
})
watch(
  () => props.show,
  (showModalVal: boolean) => (showModal.value = showModalVal)
)

function closeModal() {
  showModal.value = false
  emit('update:show', false)
}

function paymentCallback(event) {
  if (event.type === UI_PAYMENT_ELEMENT_EVENT.SUCCESS) {
    showPaymentModal.value = false
    showPaymentSuccessModal.value = true

    addUserToGroup()
  }
}
const loginPage = ref('LOGIN')
async function handleJoinGroup() {
  if (appStore.isAuthenticated) {
    joinGroupLoader.value = true
    if (
      GroupState.state.groupData?.settings?.membership?.membershipType ===
        MembershipType.PAID &&
      (!GroupState.state?.userStatus ||
        GroupState.state?.userStatus === MemberStatus.PAYMENT_INITIATED)
    ) {
      showPaymentModal.value = true
      joinGroupLoader.value = false
      return true
    }
    addUserToGroup()
  } else {
    showLoginModal.value = true
  }
}
const notification = useNotification()
function successNotificationAlert(title: string, content: string) {
  notification.success({
    title: title,
    content: content,
    duration: 10000,
  })
}
async function addUserToGroup() {
  closeModal()

  if (
    GroupState.state.groupData?.settings?.membershipQuestionsEnabled &&
    FormState.state?.membershipQuestions?.questions?.length > 0
  ) {
    showMembershipQuestions.value = true
    return
  }

  joinGroupLoader.value = true
  await GroupState.joinGroup()
  joinGroupLoader.value = false

  if (
    GroupState.state.userStatus === MemberStatus.REQUESTED &&
    GroupState.state.groupData?.settings?.membership?.membershipType !==
      MembershipType.PAID
  ) {
    successNotificationAlert(
      t('joinGroupModal.wahoo'),
      t('joinGroupModal.reviewRequestSoon')
    )
  }
}

function handleMembershipQuestionsModalClose(answersSubmitted: boolean) {
  showMembershipQuestions.value = false
  if (answersSubmitted) {
    successNotificationAlert(
      t('joinGroupModal.wahoo'),
      t('joinGroupModal.reviewRequestSoon')
    )
  }
}

const joinGroupText = computed(() => {
  if (
    GroupState.state.groupData?.settings?.membership?.membershipType ===
      MembershipType.PAID &&
    ![MemberStatus.REQUESTED, MemberStatus.PAYMENT_RECEIVED].includes(
      GroupState.state?.userStatus
    )
  ) {
    const membership = GroupState.state.groupData?.settings?.membership
    if (
      GroupState.state.groupData?.settings?.membership.trialDays > 0 &&
      membership.subscriptionType === SubscriptionType.RECURRING
    ) {
      return `${membership.trialDays} ${t('joinGroupModal.dayFreeTrial')}`
    }
    return membership.subscriptionType === SubscriptionType.RECURRING
      ? `${t('joinGroupModal.subscribeFor')} ${
          currency[membership?.currency].symbol
        }${membership.price}/${capitalizeWord(membership.subscriptionInterval)}`
      : `${t('joinGroupModal.subscribeFor')} ${
          currency[membership?.currency].symbol
        }${membership.price}`
  }

  if (
    GroupState.state.groupData?.settings?.groupType === GroupType.PRIVATE &&
    GroupState.state.userStatus === MemberStatus.REQUESTED
  ) {
    return t('joinGroupModal.requested')
  }

  return hasPendingMembershipQuestions.value
    ? 'Answer Questions'
    : t('joinGroupModal.joinGroup')
})

const showPaymentModal = ref(false)
const showPaymentSuccessModal = ref(false)
const showLoginModal = ref(false)

function handleLoginAction() {
  //Open payment modal after login for paid groups
  if (
    !GroupState.state?.role &&
    GroupState.state.groupData?.settings?.membership?.membershipType ==
      MembershipType.PAID
  ) {
    showPaymentModal.value = true
    return true
  }
  if (!GroupState.state?.role) {
    handleJoinGroup()
  }
  if (GroupState.state.userStatus === MemberStatus.ACTIVE) {
    closeModal()
  }
}

function closeLoginModal() {
  showLoginModal.value = false
  if (props.showLogin) {
    showModal.value = false
  }
}
</script>

<template>
  <UIModal
    v-model:show="showModal"
    :width="420"
    class-name="p-0"
    @update:show="closeModal"
  >
    <template #header>
      <UIModalHeader id="modal-header" type="custom" @close="closeModal">
        <template #customHeader>
          <div class="w-full">
            <div id="info-container" class="rounded-xl">
              <div class="relative max-h-[140px] w-full">
                <img
                  id="cover-image"
                  :src="coverUrl || DEFAULT_GROUP_COVER_IMAGE"
                  class="max-h-[140px] w-full rounded-t-xl object-cover"
                  :alt="t('common.ariaLabels.coverImage')"
                />
                <!-- <div
                  class="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 translate-y-[78%] transform"
                >
                  <img
                    class="info-logo h-32 w-32 rounded-full border-[3px] border-solid border-communities-border bg-communities-background"
                    :src="logoUrl"
                    :alt="t('common.ariaLabels.groupLogo')"
                  />
                </div> -->
              </div>
            </div>
          </div>
        </template>
      </UIModalHeader>
    </template>
    <div class="m-4 flex flex-col items-center justify-center">
      <UITextSmNormal class="my-2 text-center text-communities-font-primary">
        {{ actionText }}
      </UITextSmNormal>
      <div
        v-if="
          GroupState.state?.userStatus !== MemberStatus.REQUESTED ||
          (GroupState.state?.userStatus === MemberStatus.REQUESTED &&
            hasPendingMembershipQuestions)
        "
        class="mt-2 w-full"
      >
        <CommunityButton
          id="join-group__btn"
          button-type="primary"
          class="w-full"
          @click="handleJoinGroup"
          >{{ joinGroupText }}</CommunityButton
        >
        <TaxNotice
          v-if="
            GroupState.state.groupData?.settings?.membership?.membershipType ===
            MembershipType.PAID
          "
        />
      </div>
    </div>
  </UIModal>
  <PaymentModal
    v-if="showPaymentModal"
    :show="showPaymentModal"
    :price-ids="GroupState.state.groupData.settings?.membership?.paymentPriceId"
    :is-live-payment="
      GroupState.state.groupData.settings?.membership?.isLivePayment
    "
    :membership="GroupState.state.groupData?.settings?.membership"
    :product-name="GroupState?.state?.groupData?.name"
    :source-id="'group-' + GroupState?.state?.groupData?._id"
    @update:confirmed="paymentCallback"
    @update:show="showPaymentModal = false"
  />
  <PaymentSuccessfulModal
    v-if="showPaymentSuccessModal"
    :show="showPaymentSuccessModal"
    :join-group-loader="joinGroupLoader"
    @update:show="showPaymentSuccessModal = false"
  />
  <LoginModal
    v-if="showLoginModal"
    :page="loginPage"
    :location-data="app.state.locationData"
    :show="showLoginModal"
    :login-confirmation="handleLoginAction"
    @update:show="closeLoginModal()"
  />
  <MembershipQuestions
    v-if="showMembershipQuestions"
    :show="showMembershipQuestions"
    @update:show="handleMembershipQuestionsModalClose"
  />
</template>
<style scoped>
.n-button {
  height: 44px !important;
}
</style>
